import { MarketingDrawer, PaymentPlans } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { SectionContainer } from '@app/components/stateless/containers/section-container'
import { Header, Header3 } from '@app/components/general/header'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { FaqSection } from '@app/components/marketing/faq-section'
import { SyntheticEvent, useState } from 'react'
import { SwitchToggle } from '@app/components/general/switch-toggle'
import { MainButton } from '@app/components/general/price'
import { PriceTotal } from '@app/components/general/payments/total'
import { currencyConvert } from '@app/utils/ssr/currency-convert'
import { rateMap } from '@app/utils/ssr/ratemap'
import { buildFormatter } from '@app/lib/utils/price-config'

type I18nProps = {
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
  __convertor?: number
}

export const getStaticProps: GetStaticProps<I18nProps> = async (ctx) => {
  const ns = (await loadNamespaces({
    ...ctx,
    pathname: '/pricing',
  })) as I18nProps

  let data = null
  if (ns.__lang !== 'en') {
    data = await currencyConvert()
  }
  if (data && data.rates) {
    const mapper =
      ns.__lang in rateMap ? rateMap[ns.__lang as keyof typeof rateMap] : null

    if (mapper) {
      ns.__convertor = data.rates[mapper]
    }
  }
  return {
    props: ns,
    revalidate: 3600 * 12 * 2, // every 24 hours
  }
}

function Pricing({ __convertor }: { __convertor?: number }) {
  const { t, lang } = useTranslation('pricing')
  const [addCredits, setCreditsView] = useState<boolean>(false)
  const [credits, setCredits] = useState<string>('2.5')

  const onCreditsChange = (e: SyntheticEvent<HTMLInputElement>) => {
    setCredits(e.currentTarget.value)
  }

  const onToggleCredits = () => setCreditsView((x) => !x)

  const creds = credits ? Number(credits) : 0
  const selectedPrice = addCredits
    ? Number(credits ? Number(credits || 0) * 100 : 0)
    : 0

  const formatter = buildFormatter(lang, true)

  return (
    <MarketingDrawer>
      <SectionContainer container block className='mx-auto container max-w-4xl'>
        <div className='pb-6'>
          <Header>{t('heading')}</Header>
          <h2 className={'sr-only'}>{t('sub-title')}</h2>
          <Paragraph>{t('sub-details')}</Paragraph>
        </div>
        <div className='pb-5'>
          <div className='flex space-x-2'>
            <SwitchToggle
              id={'add-credits'}
              title={t(
                !addCredits
                  ? 'credits-form:add-credits'
                  : 'credits-form:hide-credits'
              )}
              checked={addCredits}
              onChange={onToggleCredits}
              titleLabel
            />
          </div>
        </div>

        {addCredits ? (
          <div className='space-y-3'>
            <Header3>{t('credits-form:add-credits')}</Header3>
            <Paragraph>{t('credits-form:credits-d')}</Paragraph>
            <div className='py-2'>
              <label className='sr-only'>{t('credits-form:add-credits')}</label>
              <input
                type={'number'}
                onChange={onCreditsChange}
                className='border dark:border-gray-700 rounded py-3 px-4'
                min={2.5}
                placeholder='2.5'
                defaultValue={2.5}
              />
              <div className='py-4'>
                <Paragraph className='text-sm'>
                  {t('credits-form:estimated', {
                    value: `${formatter.format(
                      creds * 500
                    )} - ${formatter.format(creds * 1000)}`,
                    creditValue: credits,
                  })}
                </Paragraph>
              </div>

              <PriceTotal
                price={
                  __convertor ? __convertor * selectedPrice : selectedPrice
                }
                lang={__convertor && __convertor !== 1 ? lang : undefined}
              />
            </div>
            <div className='py-2 md:w-1/2'>
              <MainButton credits={creds} navigate title={'credits'} />
            </div>
          </div>
        ) : (
          <PaymentPlans pricingPage __convertor={__convertor} />
        )}
        <div className='py-6'>
          <div className='p-4'>
            <abbr
              className='text-center text-3xl font-bold no-underline block pb-2'
              title={t('faq:header').replace(' (FAQ)', '')}
            >
              {t('common:faq')}
            </abbr>
            <div className='border-t border-dotted dark:border-gray-700'>
              <FaqSection t={t} lang={lang} headers={'h4'} blockIndex={[1]} />
            </div>
          </div>
        </div>
      </SectionContainer>
    </MarketingDrawer>
  )
}

export default metaSetter(
  { Pricing },
  {
    translation: 'pricing',
  }
)
